import axios from 'axios';
import {
  normalize_user_role,
  UPDATE_USER_URL,
  USER_LIST_URLS,
} from '@/utils/constants/constants';
import user_management_project_methods from './user_management_project_methods';

function parse_response_data(response_data) {
  return typeof response_data === 'string'
    ? JSON.parse(response_data)
    : response_data;
}

function build_user_name_fallback(profile) {
  return profile.email || 'User has not logged in';
}

function get_profile_name_part(profile, key) {
  return Reflect.get(profile || {}, key) || '';
}

function get_user_record_name_part(user, key) {
  return Reflect.get(user || {}, key) || '';
}

function get_user_name_part(profile, user, key) {
  return get_profile_name_part(profile, key) || get_user_record_name_part(user, key);
}

export default {
  async update_user() {
    if (this.edit_user_data.phone_number && !this.validate_phone_number(this.edit_user_data.phone_number)) {
      this.show_info('Please enter a valid 10-digit phone number.');
      return;
    }
    const payload = {
      user_id: this.edit_user_data.user_id,
      email: this.edit_user_data.email,
      first_name: this.edit_user_data.first_name,
      last_name: this.edit_user_data.last_name,
      phone_number: this.edit_user_data.phone_number,
      role_id: this.edit_user_data.role_id,
    };

    await axios.post(UPDATE_USER_URL, payload, { headers: this.headers }).then(({ data }) => {
      if (data.status === 'success') {
        sessionStorage.setItem('user_role', normalize_user_role(data.data.role));
        this.show_success(data.message);
        this.fetch_users();
      } else {
        this.show_error(`Error updating user: ${data.message}`);
      }
    }).catch((error) => {
      const backend_msg = error.response?.data?.message || error.response?.data?.data || error.message;
      this.show_error(`Error updating user: ${backend_msg}`);
    });
  },

  build_user_profile(user) {
    return user.user || user;
  },

  get_nested_role_value(role) {
    return role?.role || role || '';
  },

  build_user_role_value(user, profile) {
    return this.get_nested_role_value(profile.role) || this.get_nested_role_value(user.role) || '';
  },

  join_user_name(first_name, last_name) {
    return `${first_name} ${last_name}`.trim();
  },

  get_user_name_parts(user, profile) {
    const name_parts = {
      first_name: get_user_name_part(profile, user, 'first_name'),
      last_name: get_user_name_part(profile, user, 'last_name'),
    };
    return name_parts;
  },

  build_user_name(user, profile) {
    const name_parts = this.get_user_name_parts(user, profile);
    return this.join_user_name(name_parts.first_name, name_parts.last_name) || build_user_name_fallback(profile);
  },

  build_user_contact_value(primary, fallback = '') {
    return primary || fallback || '';
  },

  build_user_last_login(user, profile) {
    return this.format_date(profile.last_login ?? user.last_login ?? null, 'Never logged in');
  },

  build_display_role(role_value) {
    return role_value === 'Entity' ? 'Founder' : role_value;
  },

  build_user_identifier_value(user, profile) {
    return user.user_id || user.id || profile.id;
  },

  build_user_row_identifiers(user, profile) {
    const identifiers = {
      is_active: user.is_active ?? profile.is_active ?? false,
      user_id: this.build_user_identifier_value(user, profile),
      email: this.build_user_contact_value(profile.email, user.email),
      phone_number: this.build_user_contact_value(user.phone_number, profile.phone_number),
    };
    return identifiers;
  },

  build_user_row(user, index) {
    const profile = this.build_user_profile(user);
    const role_value = this.build_user_role_value(user, profile);
    const name = this.build_user_name(user, profile);
    const { is_active, user_id, email, phone_number } = this.build_user_row_identifiers(user, profile);
    const last_login = this.build_user_last_login(user, profile);
    const role = this.build_display_role(role_value);

    const row = {
      __row_id: user_id,
      user_id,
      name,
      email,
      phone_number,
      last_login,
      role,
      is_active,
      'User ID': index + 1,
      Name: name,
      Email: email,
      'Phone Number': phone_number,
      'Last Login': last_login,
      Role: role,
      'Is Active': is_active ? 'Yes' : 'No',
    };
    return row;
  },

  async fetch_users() {
    this.error_message = '';
    const project_id = Number(this.project_id);
    const payload = { page_size: 1000 };

    if (Number.isFinite(project_id) && project_id > 0) {
      payload.project_id = project_id;
    }

    return axios.get(USER_LIST_URLS, {
      headers: this.headers,
      params: payload,
    }).then((response) => {
      const res = parse_response_data(response.data);
      this.users = (res.data || []).map((user, index) => this.build_user_row(user, index));
      this.users_list = this.users;
      this.table_headers = ['User ID', 'Name', 'Email', 'Phone Number', 'Last Login', 'Role', 'Is Active'];
    }).catch((error) => {
      this.error_message = `Error fetching users: ${error.message}`;
    });
  },

  ...user_management_project_methods,
};
